import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { PaperPlaneTilt, ShieldCheck, Sparkle, ArrowLeft, Robot } from "@phosphor-icons/react";
import AvatarUploader from "../components/AvatarUploader";

export default function AgentDetail() {
  const { key } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      const a = await api.get(`/agents/${key}`);
      setAgent(a.data);
      if (user && user.id) {
        const h = await api.get(`/agents/${key}/chat/history`);
        setMessages(h.data || []);
      }
    })();
  }, [key, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    if (!user || !user.id) { nav("/login", { state: { from: `/agent/${key}` } }); return; }
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { id: `temp-${Date.now()}`, role: "user", text, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/agents/${key}/chat`, { message: text });
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("temp-")), data.user_message, data.agent_message]);
    } catch (err) {
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "agent", text: "⚠️  My circuits hit a snag. Try again?", created_at: new Date().toISOString() }]);
    } finally {
      setBusy(false);
    }
  };

  if (!agent) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />BOOTING AGENT...</div></AppShell>;

  const avatarUrl = agent.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${agent.avatar_url}` : null;
  const isAdmin = user && user.role === "admin";

  const saveAgentAvatar = async (url) => {
    try {
      await api.patch(`/agents/${agent.key}/avatar`, { avatar_url: url });
      setAgent({ ...agent, avatar_url: url });
    } catch (_) {}
  };

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="agent-detail-page">
        <aside className="lg:col-span-4 space-y-5">
          <button onClick={() => nav("/agents")} className="btn btn-ghost btn-sm" data-testid="agent-back-btn">
            <ArrowLeft size={12} weight="bold" /> All agents
          </button>

          <div className="border border-[#27272A] bg-[#121214]" style={{ borderTop: `3px solid ${agent.color}` }}>
            <div className="aspect-square bg-[#0A0A0C] relative">
              {avatarUrl ? (
                <img src={avatarUrl} alt={agent.name} className="w-full h-full object-cover" data-testid="agent-avatar" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display text-7xl" style={{ background: agent.color, color: "#000", fontWeight: 900 }}>
                  {agent.name[0]}
                </div>
              )}
              {isAdmin && (
                <div className="absolute bottom-3 right-3">
                  <AvatarUploader currentUrl={agent.avatar_url} onUploaded={saveAgentAvatar} size={56} label="Change" />
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="label-mono mb-2" style={{ color: agent.color }}>{agent.role.toUpperCase()}</div>
              <div className="font-display text-3xl text-white mb-3" style={{ fontWeight: 800, letterSpacing: "-0.025em" }} data-testid="agent-name">
                {agent.name}
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="label-mono flex items-center gap-1" style={{ color: "#00E676" }}>
                  <ShieldCheck size={11} weight="fill" /> TRUST {agent.trust_score}
                </span>
                <span className="label-mono">·</span>
                <span className="label-mono">AI PERSONA</span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">{agent.specialty}.</p>
              <div className="border-t border-[#27272A] pt-3 text-xs text-zinc-400 leading-relaxed mb-4">
                <div className="label-mono mb-1.5">PERSONALITY</div>
                {agent.personality}
              </div>
              <div className="border-t border-[#27272A] pt-3 text-xs text-zinc-400 leading-relaxed">
                <div className="label-mono mb-1.5">DOMAINS</div>
                <div className="flex flex-wrap gap-1.5">
                  {agent.hubs.map((h) => (
                    <span key={h} className="label-mono px-2 py-0.5 border border-[#27272A]" style={{ fontSize: 9 }}>{h.toUpperCase()}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-8 flex flex-col" style={{ height: "calc(100vh - 8rem)" }}>
          <div className="border border-[#27272A] bg-[#0A0A0C] flex-1 flex flex-col">
            <div className="px-5 py-3 border-b border-[#27272A] flex items-center justify-between">
              <div className="flex items-center gap-2 label-mono">
                <Sparkle size={11} weight="fill" style={{ color: agent.color }} />
                LIVE CHAT · 1:1 WITH {agent.name.toUpperCase()}
              </div>
              <div className="hub-dot pulse" style={{ color: "#00E676" }} />
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-scroll">
              {messages.length === 0 && (
                <div className="text-center py-10 text-zinc-500">
                  <Robot size={32} weight="light" className="mx-auto mb-3" style={{ color: agent.color }} />
                  <div className="label-mono mb-2">CHANNEL OPEN</div>
                  <p className="text-sm">Ask {agent.name} anything about {agent.specialty.split(",")[0]}.</p>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
                  <div className="shrink-0">
                    {m.role === "agent" ? (
                      avatarUrl ? (
                        <img src={avatarUrl} alt="" className="w-8 h-8 object-cover border border-[#27272A]" />
                      ) : (
                        <div className="w-8 h-8 flex items-center justify-center font-display text-xs" style={{ background: agent.color, color: "#000" }}>
                          {agent.name[0]}
                        </div>
                      )
                    ) : (
                      <div className="w-8 h-8 bg-[#E60012] flex items-center justify-center font-display text-xs text-white">
                        {user?.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                  </div>
                  <div className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "agent" ? "bg-[#121214] border border-[#27272A] text-zinc-100" : "bg-[#E60012] text-white"
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex gap-3" data-testid="chat-typing">
                  <div className="shrink-0">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="w-8 h-8 object-cover border border-[#27272A]" />
                    ) : (
                      <div className="w-8 h-8" style={{ background: agent.color }} />
                    )}
                  </div>
                  <div className="px-4 py-3 bg-[#121214] border border-[#27272A] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: agent.color }} />
                    <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: agent.color, animationDelay: "200ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: agent.color, animationDelay: "400ms" }} />
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="p-4 border-t border-[#27272A] flex gap-2" data-testid="chat-form">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={user && user.id ? `Message ${agent.name}...` : "Sign in to chat with the agent"}
                disabled={!user || !user.id || busy}
                className="input flex-1"
                data-testid="chat-input"
              />
              {user && user.id ? (
                <button type="submit" disabled={busy || !input.trim()} className="btn btn-primary" data-testid="chat-send-btn">
                  <PaperPlaneTilt size={14} weight="bold" />
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary" data-testid="chat-signin-btn">Sign in</Link>
              )}
            </form>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
